import React, { useState, useEffect } from "react";
import { withRouter, Link, useHistory, useLocation } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import logo from "../assets/images/logo3.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSignOutAlt,
  faBars,
  faTimes,
  faShieldAlt,
  faChevronDown,
  faUsers,
  faUserTie,
  faStore,
  faNewspaper,
} from "@fortawesome/free-solid-svg-icons";

const NAV_BG = "#1a3328";
const NAV_PILL = "#2d5a3d";
const NAV_DIM = "#7fab8e";
const NAV_TEXT = "#e8f0eb";

const navItems = [
  { label: "Dashboard", path: "/" },
  { label: "Farmer", path: "/farmer" },
  { label: "Crop", path: "/crop" },
  { label: "Products", path: "/productAndLabel" },
  { label: "Commodity News", path: "/commodity-news", icon: faNewspaper },
  { label: "Reports", path: "/reports" },
];

const Navigation = () => {
  const history = useHistory();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const isAdmin = user?.role === "admin";

  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  const initials = user?.fullName
    ? user.fullName
        .trim()
        .split(/\s+/)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "U";

  const onLogout = () => {
    localStorage.setItem("accessToken", "");
    localStorage.setItem("user", "");
    history.push("/login");
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Track viewport width
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const adminSubItems = [
    { label: "Operator", path: "/signUp", icon: faUsers },
    { label: "Staff Details", path: "/administration/staff", icon: faUserTie },
    { label: "Dealer Details", path: "/administration/dealer", icon: faStore },
  ];

  const isAdminActive = adminSubItems.some((item) => isActive(item.path));

  const allNavItems = [...navItems];

  return (
    <>
      <style>{`
        .nav-pill-link:hover {
          background-color: ${NAV_PILL} !important;
          color: #fff !important;
        }
        .mobile-menu-link:hover {
          background-color: rgba(255,255,255,0.08) !important;
        }
        .nav-avatar:hover {
          opacity: 0.85;
        }
      `}</style>

      <nav style={s.bar}>
        {/* ── Logo ── */}
        <Link to="/" style={{ textDecoration: "none", flexShrink: 0 }}>
          <img src={logo} alt="Manshya" style={s.logo} />
        </Link>

        {/* ── Desktop Nav pills ── */}
        {!isMobile && (
          <div style={s.pills}>
            {allNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="nav-pill-link"
                style={{
                  ...s.pill,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  backgroundColor: isActive(item.path)
                    ? NAV_PILL
                    : "transparent",
                  fontWeight: isActive(item.path) ? 600 : 400,
                  borderBottom: isActive(item.path)
                    ? "2px solid #5ecf87"
                    : "2px solid transparent",
                }}
              >
                {item.icon && (
                  <FontAwesomeIcon icon={item.icon} style={{ fontSize: "0.75rem" }} />
                )}
                {item.label}
              </Link>
            ))}

            {/* Administration dropdown — admin only */}
            {isAdmin && (
              <Dropdown align="start">
                <Dropdown.Toggle
                  as="div"
                  bsPrefix="none"
                  className="nav-pill-link"
                  style={{
                    ...s.pill,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    userSelect: "none",
                    backgroundColor: isAdminActive ? NAV_PILL : "transparent",
                    fontWeight: isAdminActive ? 600 : 400,
                    borderBottom: isAdminActive
                      ? "2px solid #5ecf87"
                      : "2px solid transparent",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faShieldAlt}
                    style={{ fontSize: "0.75rem" }}
                  />
                  Administration
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    style={{ fontSize: "0.6rem", marginLeft: 2 }}
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu style={s.adminMenu}>
                  {adminSubItems.map((item) => (
                    <Dropdown.Item
                      key={item.path}
                      as={Link}
                      to={item.path}
                      style={{
                        ...s.adminMenuItem,
                        backgroundColor: isActive(item.path)
                          ? "#e8f5ee"
                          : "transparent",
                        color: isActive(item.path) ? "#2d5a3d" : "#1a2e22",
                        fontWeight: isActive(item.path) ? 600 : 400,
                      }}
                    >
                      <FontAwesomeIcon
                        icon={item.icon}
                        style={{
                          fontSize: "0.75rem",
                          marginRight: 8,
                          color: "#2d5a3d",
                        }}
                      />
                      {item.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        )}

        {/* ── Right side ── */}
        <div style={s.rightGroup}>
          {/* Desktop user avatar */}
          {!isMobile && (
            <Dropdown align="end">
              <Dropdown.Toggle
                as="div"
                bsPrefix="none"
                style={s.avatar}
                className="nav-avatar"
              >
                {initials}
              </Dropdown.Toggle>
              <Dropdown.Menu style={s.dropMenu}>
                <div style={s.dropHeader}>
                  <div style={s.dropName}>{user?.fullName || "User"}</div>
                  <div style={s.dropRole}>
                    {isAdmin ? "Administrator" : "Operator"}
                  </div>
                </div>
                <Dropdown.Divider style={{ margin: "0.25rem 0" }} />
                <Dropdown.Item onClick={onLogout} style={s.dropLogout}>
                  <FontAwesomeIcon
                    icon={faSignOutAlt}
                    style={{ marginRight: "0.4rem" }}
                  />
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <button
              style={s.hamburger}
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} />
            </button>
          )}
        </div>
      </nav>

      {/* ── Mobile slide-down menu ── */}
      {isMobile && menuOpen && (
        <div style={s.mobileMenu}>
          {/* User info strip */}
          <div style={s.mobileUserStrip}>
            <div style={s.mobileAvatar}>{initials}</div>
            <div>
              <div
                style={{ fontWeight: 700, color: "#fff", fontSize: "0.9rem" }}
              >
                {user?.fullName || "User"}
              </div>
              <div style={{ fontSize: "0.75rem", color: NAV_DIM }}>
                {isAdmin ? "Administrator" : "Operator"}
              </div>
            </div>
          </div>

          <div style={s.mobileDivider} />

          {/* Nav links */}
          {allNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="mobile-menu-link"
              style={{
                ...s.mobileLink,
                display: "flex",
                alignItems: "center",
                gap: 8,
                backgroundColor: isActive(item.path) ? NAV_PILL : "transparent",
                fontWeight: isActive(item.path) ? 600 : 400,
                borderLeft: isActive(item.path)
                  ? "3px solid #5ecf87"
                  : "3px solid transparent",
              }}
            >
              {item.icon && (
                <FontAwesomeIcon icon={item.icon} style={{ fontSize: "0.75rem", color: "#7fab8e" }} />
              )}
              {item.label}
            </Link>
          ))}

          {/* Administration section — admin only */}
          {isAdmin && (
            <>
              <div style={s.mobileDivider} />
              <div
                style={{
                  padding: "0.5rem 1.5rem 0.25rem",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  color: "#7fab8e",
                  textTransform: "uppercase",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FontAwesomeIcon
                  icon={faShieldAlt}
                  style={{ fontSize: "0.65rem" }}
                />
                Administration
              </div>
              {adminSubItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="mobile-menu-link"
                  style={{
                    ...s.mobileLink,
                    paddingLeft: "2rem",
                    backgroundColor: isActive(item.path)
                      ? NAV_PILL
                      : "transparent",
                    fontWeight: isActive(item.path) ? 600 : 400,
                    borderLeft: isActive(item.path)
                      ? "3px solid #5ecf87"
                      : "3px solid transparent",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FontAwesomeIcon
                    icon={item.icon}
                    style={{ fontSize: "0.75rem", color: "#7fab8e" }}
                  />
                  {item.label}
                </Link>
              ))}
            </>
          )}

          <div style={s.mobileDivider} />

          {/* Logout */}
          <button style={s.mobileLogout} onClick={onLogout}>
            <FontAwesomeIcon
              icon={faSignOutAlt}
              style={{ marginRight: "0.5rem" }}
            />
            Logout
          </button>
        </div>
      )}

      {/* Fixed-navbar spacer */}
      <div style={{ height: 80 }} />
    </>
  );
};

const s = {
  bar: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: NAV_BG,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 1.25rem",
    height: 80,
    boxShadow: "0 2px 16px rgba(0,0,0,0.35)",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  logo: { height: 70, objectFit: "contain" },
  pills: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    flex: 1,
    justifyContent: "center",
  },
  pill: {
    padding: "6px 14px",
    borderRadius: 6,
    textDecoration: "none",
    fontSize: "0.875rem",
    color: NAV_TEXT,
    transition: "background-color 0.2s, border-color 0.2s",
    whiteSpace: "nowrap",
  },
  rightGroup: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexShrink: 0,
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    backgroundColor: NAV_PILL,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85rem",
    fontWeight: 700,
    cursor: "pointer",
    border: "2px solid #3d8c5e",
    userSelect: "none",
    transition: "opacity 0.2s",
  },
  dropMenu: {
    minWidth: 200,
    border: "none",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    borderRadius: 10,
    padding: "0.5rem 0",
  },
  dropHeader: { padding: "0.6rem 1rem 0.4rem" },
  dropName: { fontWeight: 700, color: "#1a3328", fontSize: "0.9rem" },
  dropRole: { fontSize: "0.75rem", color: "#888" },
  dropLogout: { color: "#e74c3c", fontSize: "0.875rem" },

  adminMenu: {
    minWidth: 200,
    border: "none",
    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
    borderRadius: 10,
    padding: "0.4rem 0",
    marginTop: 6,
  },
  adminMenuItem: {
    fontSize: "0.85rem",
    padding: "0.55rem 1rem",
    borderRadius: 6,
    margin: "0 0.3rem",
    transition: "background 0.12s",
  },

  // Mobile
  hamburger: {
    background: "none",
    border: "none",
    color: NAV_TEXT,
    fontSize: "1.2rem",
    cursor: "pointer",
    padding: "6px 8px",
    lineHeight: 1,
  },
  mobileMenu: {
    position: "fixed",
    top: 80,
    left: 0,
    right: 0,
    zIndex: 999,
    backgroundColor: NAV_BG,
    boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
    borderTop: "1px solid rgba(255,255,255,0.08)",
    display: "flex",
    flexDirection: "column",
  },
  mobileUserStrip: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "1rem 1.25rem",
  },
  mobileAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: NAV_PILL,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.9rem",
    fontWeight: 700,
    flexShrink: 0,
    border: "2px solid #3d8c5e",
  },
  mobileDivider: {
    height: 1,
    backgroundColor: "rgba(255,255,255,0.08)",
    margin: "0 1.25rem",
  },
  mobileLink: {
    display: "block",
    padding: "0.85rem 1.5rem",
    textDecoration: "none",
    color: NAV_TEXT,
    fontSize: "0.95rem",
    transition: "background-color 0.15s",
  },
  mobileLogout: {
    background: "none",
    border: "none",
    color: "#e87070",
    fontSize: "0.9rem",
    padding: "0.85rem 1.5rem",
    textAlign: "left",
    cursor: "pointer",
    width: "100%",
    marginBottom: 8,
  },
};

export default withRouter(Navigation);
